import React, { useEffect, useMemo, useRef, useState } from "react";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import horarioBonito from "../../../../utils/horarioBonito";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import TiraDeMeses from "../../../../components/acuerdos/tiraDeMeses";
import Grupos from "./grupos";
import Limpieza from "./limpieza";
import "./promotions.css";
import { EditorPromo, CopiarPromo, ReportePromos, GRUPOS, VINCULOS, AUDIENCIAS } from "./editorPromo";
import { CuentaRegresiva, CupoTexto } from "../../../../components/acuerdos/cuentaRegresiva";

/*
	Descuentos (Finanzas) — Etapa 3 de cobros (docs/ESPEC_COBROS_UNIFICADO.md, decisión 17 de Esteban).
	- Catálogo: las promociones del plantel (WA_Promociones.md vuelto datos). Lo editan Dirección de Ventas del país y
	  superadmin, con motivo; cada cambio queda en el historial. Un cambio NO mueve los acuerdos ya firmados.
	- Acuerdos: el acuerdo comercial de cada matrícula (nace al aprobar la solicitud), con su tira de meses, con quién
	  va, su estado e historial. Anularlo (Supervisión de Caja, Dirección de Ventas o superadmin, con motivo) devuelve los meses que se pueden
	  tocar a como estaban; lo pagado no se toca.
	- Grupos (Etapa 4): lo que la vigilancia propone («Por resolver») y los grupos vivos con sus miembros y alertas.
	- Limpieza (Etapa 5): las matrículas activas sin acuerdo con lo que WAPP infiere de lo pagado; Caja del plantel (A5; antes Admisiones y Dirección de
	  Ventas) confirman en bloque las de confianza alta y una por una las dudosas. Nada sube solo.
	Todo lo valida el servidor.
*/

const http = HttpManager.getInstance();
const fecha = (d: string | null | undefined) => d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "";
const num = (n: number) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { maximumFractionDigits: 2 });


type Promo = any;

/** "10%", "precio fijo 1,000", "−500", "regala los últimos 2". */
export const valorTexto = (p: Promo, sym: string): string => {
	const r = p.rules || {};
	if (r.monthsPaid && r.monthsFree) return `paga ${r.monthsPaid} → regala ${r.monthsFree}`;
	if (r.freeLast) return `paga ${r.monthsTotal ? r.monthsTotal - r.freeLast : "los primeros"} → regala ${r.freeLast}`;
	if (r.freeSecond) return "paga 1 → el 2do gratis";
	if (p.calcType === "percent") return (p.value >= 100 ? "gratis" : num(p.value) + "%") + (r.monthFrom ? ` (mes ${r.monthFrom}${r.monthTo && r.monthTo !== r.monthFrom ? "–" + r.monthTo : ""})` : "");
	if (p.calcType === "fixed_price") return `precio fijo ${sym}${num(p.value)}`;
	return `−${sym}${num(p.value)}`;
};

const Historial: React.FC<{ campusId: number; promo: Promo; sym: string }> = ({ campusId, promo, sym }) => {
	const [list, setList] = useState<any[] | null>(null);
	useEffect(() => { http.paymentService.promotionHistory(campusId, promo.id).then((r: any) => setList(r.data || [])).catch(() => setList([])); }, [campusId, promo.id]);
	if (list === null) return <div className="wdTenue">…</div>;
	if (!list.length) return <div className="wdTenue" style={{ fontSize: 13 }}>Sin cambios desde que se cargó de WA_Promociones.md.</div>;
	return <>{list.map((h) => (
		<div key={"h" + h.id} className="wdEvento"><span className="wdTenue">{fechaHoraAmPm(h.at)} · {(h.by || "").trim() || (h.action === "migrar" ? "Sistema (migración)" : "Sistema")}</span> · {h.action}
			{/* El monto solo si cambió (las entradas de solo texto —guía, migración— decían «−$0 → −$0»). */}
			{h.before && h.after && h.before.value !== undefined && h.after.value !== undefined && valorTexto(h.before, sym) !== valorTexto(h.after, sym)
				? <> · {valorTexto(h.before, sym)} → {valorTexto(h.after, sym)}</> : null}
			{h.before && h.after && h.before.active !== undefined && h.after.active !== undefined && h.before.active !== h.after.active ? (h.after.active ? " · activada" : " · desactivada") : null}
			{h.reason ? <> · «{h.reason}»</> : null}</div>
	))}</>;
};

const PromotionsController: React.FC = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [tab, setTab] = useState<"catalogo" | "acuerdos" | "grupos" | "limpieza" | "reporte">(() => (window.location.search.indexOf("vista=grupos") >= 0 ? "grupos"
		: window.location.search.indexOf("vista=limpieza") >= 0 ? "limpieza" : "catalogo"));
	const [porResolver, setPorResolver] = useState<number>(0);
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [editing, setEditing] = useState<number | "new" | null>(null);
	const [hist, setHist] = useState<number | null>(null);
	const [copiando, setCopiando] = useState<number | null>(null);
	const [verTodas, setVerTodas] = useState(false);
	const [agreements, setAgreements] = useState<any[] | null>(null);
	const [filtro, setFiltro] = useState<{ status: string; q: string }>({ status: "vivos", q: "" });
	const [abierto, setAbierto] = useState<any>(null);
	const [anular, setAnular] = useState<string>("");

	const load = () => {
		if (!campusId) return;
		http.paymentService.promotions(campusId).then((r: any) => { setData(r.data); setError(null); }).catch((e: any) => setError(serverError(e)));
	};
	const loadAgreements = () => {
		if (!campusId) return;
		http.paymentService.agreements(campusId, filtro).then((r: any) => setAgreements(r.data || [])).catch((e: any) => setError(serverError(e)));
	};
	useEffect(() => { setData(null); setAgreements(null); setAbierto(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId]);
	useEffect(() => {
		if (!campusId) return;
		http.paymentService.discountGroupsCount(campusId).then((r: any) => setPorResolver(r.data.open || 0)).catch(() => setPorResolver(0));
	}, [campusId]);
	useEffect(() => {
		if (tab !== "acuerdos") return;
		const t = setTimeout(loadAgreements, 300);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab, campusId, filtro.status, filtro.q]);

	const sym = data?.currencySymbol || "";
	const campusName = legacyCampus?.name || sessionCampus?.name || "";
	const grupos = useMemo(() => {
		const list = (data?.promotions || []).filter((p: Promo) => verTodas || p.validToday);
		const out: Record<string, Promo[]> = {};
		list.forEach((p: Promo) => { const k = p.scope === "caja" ? "caja" : p.group; (out[k] = out[k] || []).push(p); });
		return out;
	}, [data, verTodas]);

	const abrir = async (id: number) => {
		if (abierto && abierto.id === id) { setAbierto(null); return; }
		try { const r = await http.paymentService.agreement(campusId!, id); setAbierto(r.data); setAnular(""); }
		catch (e) { setError(serverError(e)); }
	};
	// «Aprobar de todos modos» un multimes / 2x1 que ya no puede cumplirse (ESPEC_AUDITORIA_PREGUNTAS B). Un envío a la vez.
	const [excMotivo, setExcMotivo] = useState<string>("");
	const [excBusy, setExcBusy] = useState<boolean>(false);
	const excEnviando = useRef<boolean>(false);
	const doExcepcion = async () => {
		if (!abierto || !campusId || excEnviando.current) return;
		excEnviando.current = true;
		setExcBusy(true);
		try {
			const r = await http.paymentService.approveAdvanceException(campusId, abierto.id, excMotivo.trim(), "promotions");
			if (r.data?.agreement) setAbierto(r.data.agreement);
			setExcMotivo(""); setError(r.data?.result?.text || null); loadAgreements();
		} catch (e) {
			setError(serverError(e));
		} finally {
			excEnviando.current = false;
			setExcBusy(false);
		}
	};
	const doAnular = async () => {
		if (!abierto || !campusId) return;
		if (!window.confirm("¿Anular el acuerdo «" + abierto.promoName + "»? Los meses pendientes vuelven a su precio de antes; lo pagado no se toca.")) return;
		try { const r = await http.paymentService.cancelAgreement(campusId, abierto.id, anular.trim()); setAbierto(r.data); loadAgreements(); }
		catch (e) { setError(serverError(e)); }
	};

	if (!campusId) return <div className="container-fluid waModulo waDescuentos" style={{ padding: 24 }}>Elige un plantel.</div>;
	const fila = (p: Promo) => (
		<React.Fragment key={"p" + p.id}>
			<div className={"wdFila" + (p.validToday ? "" : " wdFilaOff")}>
				<div className="wdNombre"><b>{p.name}</b><small>{p.conditions}</small>
					{p.courseSummary && p.courseSummary !== "Todos los cursos" ? <small>{p.courseSummary}</small> : null}
					{(p.weekdays || []).length || p.timeFrom ? <small>{(p.weekdays || []).length ? "Solo " + (p.weekdays || []).map((n: number) => ["", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"][n]).join(", ") : ""}
						{p.timeFrom ? " · " + p.timeFrom + "–" + (p.timeTo || "") : ""}</small> : null}
					<span style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
						{p.window?.countdownToIso ? <CuentaRegresiva toIso={p.window.countdownToIso} serverNowIso={p.window.serverNowIso} onExpire={load} compacta /> : null}
						<CupoTexto quota={p.window?.quota} compacta />
						{p.givenBy === "supervisora" ? <span className="wdChip wdChipFilo">Solo Supervisora</span> : null}
						{p.requiresApproval ? <span className="wdChip wdChipFilo">Con Aprobación</span> : null}
					</span>
				</div>
				<div>{valorTexto(p, sym)}{p.linkType ? <div><span className="wdChip wdChipFilo">{VINCULOS[p.linkType]}{p.groupSize ? " de " + p.groupSize : ""}</span></div> : null}{p.audience ? <span className="wdChip wdChipFilo">{AUDIENCIAS[p.audience]}</span> : null}</div>
				<div>
					{!p.active ? <span className="wdChip wdChipOro">Desactivada</span> : !p.validToday ? <span className="wdChip wdChipOro">{p.validTo ? "Venció " + fecha(p.validTo) : "Empieza " + fecha(p.validFrom)}</span>
						: <span className="wdChip wdChipAcento">{p.validTo ? "Hasta " + fecha(p.validTo) : "Estable"}</span>}
					{p.liveAgreements ? <div className="wdTenue" style={{ fontSize: 12 }}>{p.liveAgreements === 1 ? "1 acuerdo vivo" : p.liveAgreements + " acuerdos vivos"}</div> : null}
				</div>
				<div className="wdAcc">
					<button type="button" className="wdLink" onClick={() => setHist(hist === p.id ? null : p.id)}>Historial</button>
					{data.canEdit ? <button type="button" className="wdLink" onClick={() => setEditing(p.id)}>Cambiar</button> : null}
					{data.canEdit ? <button type="button" className="wdLink" onClick={() => setCopiando(copiando === p.id ? null : p.id)}>Copiar</button> : null}
				</div>
			</div>
			{hist === p.id ? <div style={{ padding: "4px 8px 10px" }}><Historial campusId={campusId} promo={p} sym={sym} /></div> : null}
			{editing === p.id ? <EditorPromo campusId={campusId} promo={p} cursos={data.catalogCourses} codigos={data.codes || {}} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /> : null}
			{copiando === p.id ? <CopiarPromo campusId={campusId} promo={p} campuses={data.campuses || []} onDone={() => {}} /> : null}
		</React.Fragment>
	);

	return (
		<div className="container-fluid waModulo waDescuentos" style={{ padding: movil ? "10px 12px 24px 12px" : "10px 24px 32px 24px" }}>
			<div className="wdCabeza">
				<h4 className="wdTitulo">Descuentos</h4>
				<span className="wdTenue">{campusName}{data ? ` · hoy ${fecha(data.today)}` : ""}</span>
			</div>
			<div className="wdTabs">
				<button type="button" className={"wdTab" + (tab === "catalogo" ? " wdTabOn" : "")} onClick={() => setTab("catalogo")}>Catálogo</button>
				<button type="button" className={"wdTab" + (tab === "acuerdos" ? " wdTabOn" : "")} onClick={() => setTab("acuerdos")}>Acuerdos por Matrícula</button>
				<button type="button" className={"wdTab" + (tab === "grupos" ? " wdTabOn" : "")} onClick={() => setTab("grupos")}>
					Grupos{porResolver > 0 ? <span className="wdChip wdChipOro" style={{ marginLeft: 6 }}>{porResolver} por resolver</span> : null}</button>
				<button type="button" className={"wdTab" + (tab === "limpieza" ? " wdTabOn" : "")} onClick={() => setTab("limpieza")}>Limpieza</button>
				<button type="button" className={"wdTab" + (tab === "reporte" ? " wdTabOn" : "")} onClick={() => setTab("reporte")}>Reporte</button>
			</div>
			{error ? <div className="wdAviso">{error}</div> : null}

			{tab === "reporte" ? <ReportePromos campusId={campusId} sym={sym} /> : tab === "limpieza" ? <Limpieza campusId={campusId} sym={sym} /> : tab === "grupos" ? <Grupos campusId={campusId} sym={sym} onCount={setPorResolver} /> : tab === "catalogo" ? (!data ? <div className="loader" /> : (
				<>
					<div className="wdFiltros">
						<label className="wdVerTodas"><input type="checkbox" checked={verTodas} onChange={(e) => setVerTodas(e.target.checked)} /> <span>Ver también las vencidas y desactivadas</span></label>
						{data.canEdit && editing !== "new" ? <button type="button" className="wdBoton wdPrimario" onClick={() => setEditing("new")}>Nueva Promoción</button> : null}
					</div>
					{editing === "new" ? <EditorPromo campusId={campusId} promo={null} cursos={data.catalogCourses} codigos={data.codes || {}} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /> : null}
					{["mensualidad", "inscripcion", "referido", "caja"].filter((k) => (grupos[k] || []).length).map((k) => (
						<React.Fragment key={k}>
							<h5 className="wdSeccion">{k === "caja" ? "Promociones de Caja (al cobrar: adelanto, kits)" : GRUPOS[k] + (k === "mensualidad" ? " · se eligen al matricular" : "")}</h5>
							{grupos[k].map(fila)}
						</React.Fragment>
					))}
				</>
			)) : (
				<>
					<div className="wdFiltros">
						<select className="form-select form-select-sm" style={{ width: "auto", maxWidth: "100%" }} value={filtro.status} onChange={(e) => setFiltro({ ...filtro, status: e.target.value })}>
							<option value="vivos">Vigentes y Pendientes de Adelanto</option><option value="">Todos</option>
						</select>
						<input className="form-control form-control-sm" placeholder="Buscar alumno" value={filtro.q} onChange={(e) => setFiltro({ ...filtro, q: e.target.value })} />
					</div>
					{agreements === null ? <div className="loader" /> : !agreements.length ? <div className="wdTenue">Sin acuerdos todavía: nacen al aprobar una solicitud con su paso Promoción.</div> : agreements.map((a) => (
						<React.Fragment key={"a" + a.id}>
							<div className="wdAcuerdo" onClick={() => abrir(a.id)}>
								<div><b>{a.name}</b><div className="wdTenue" style={{ fontSize: 12 }}>{a.courseName} · {horarioBonito(a.scheduleName)}</div></div>
								<div>{a.promoName}{a.roleName ? <span className="wdTenue"> · {a.roleName}</span> : null}{a.omitted ? <div className="wdOro" style={{ fontSize: 12 }}>{a.omitted === 1 ? "1 mes sin tocar" : a.omitted + " meses sin tocar"}</div> : null}</div>
								<div><span className={"wdChip " + (a.statusId === 2 ? "wdChipOro" : a.statusId <= 3 ? "wdChipAcento" : "wdChipFilo")}>{a.status}</span></div>
								<div className="wdTenue" style={{ fontSize: 12 }}>{fecha((a.createdAt || "").slice(0, 10))}{a.requestId ? " · #" + a.requestId : ""}</div>
							</div>
							{abierto && abierto.id === a.id ? (
								<div className="wdDetalle">
									<b>{abierto.name}</b> · {abierto.promoName} <span className={"wdChip " + (abierto.statusId === 2 ? "wdChipOro" : abierto.statusId <= 3 ? "wdChipAcento" : "wdChipFilo")}>{abierto.status}</span>
									{abierto.conditions?.text ? <div className="wdOro" style={{ fontSize: 13 }}>{abierto.conditions.text}</div> : null}
									{abierto.endReason ? <div className="wdTenue" style={{ fontSize: 13 }}>{abierto.endedAt?.replace("T", " ")} · {abierto.endReason}</div> : null}
									{abierto.members.length ? <div style={{ fontSize: 14, marginTop: 4 }}>Grupo: {abierto.members.map((m: any) => m.name + " (" + m.roleName + (m.left ? ", salió" : "") + ")").join(" · ")}</div> : null}
									<TiraDeMeses meses={abierto.months.map((m: any) => ({ n: m.n, title: m.title, state: m.statusId === 2 ? "pagado" : m.state, subTotal: m.subTotal, list: m.list, note: m.applied === "omitido" ? m.note : null }))} sym={sym} />
									{abierto.months.filter((m: any) => m.applied === "omitido").map((m: any) => <div key={"o" + m.tuitionId} className="wdOro" style={{ fontSize: 13 }}>{m.title}: {m.note}</div>)}
									<div style={{ marginTop: 8 }}>
										{abierto.events.map((e: any, i: number) => <div key={"e" + i} className="wdEvento"><span className="wdTenue">{fechaHoraAmPm(e.at)} · {(e.by || "").trim() || "Sistema"}</span> · {e.notes}</div>)}
									</div>
									{abierto.exception ? <div className="wdNota" style={{ fontSize: 13 }}>Excepción aprobada por {abierto.exception.by} · {fechaHoraAmPm(abierto.exception.at)} · {abierto.exception.reason}</div> : null}
									{abierto.blocked && !abierto.exception ? <div className="wdAviso" style={{ fontSize: 13 }}>{abierto.blocked}</div> : null}
									{abierto.blocked && abierto.canApproveException ? (
										<div className="wdDecidir" style={{ marginTop: 8 }}>
											<textarea className="form-control" rows={2} style={{ fontSize: 16, flex: "1 1 260px", maxWidth: 480 }} placeholder="Motivo para aprobarlo de todos modos (queda en Auditoría)" value={excMotivo} disabled={excBusy} onChange={(e) => setExcMotivo(e.target.value)} />
											<button type="button" className="wdBoton wdPrimario" style={{ minHeight: 40 }} disabled={excBusy || excMotivo.trim().length < 10} onClick={doExcepcion}>{excBusy ? "Enviando…" : "Aprobar de Todos Modos"}</button>
										</div>
									) : null}
									{abierto.canCancel ? (
										<div className="wdFiltros" style={{ marginTop: 10 }}>
											<input className="form-control form-control-sm" placeholder="Motivo para anular (queda en el historial)" value={anular} onChange={(e) => setAnular(e.target.value)} />
											<button type="button" className="wdBoton" disabled={anular.trim().length < 10} onClick={doAnular}>Anular Acuerdo</button>
										</div>
									) : null}
								</div>
							) : null}
						</React.Fragment>
					))}
				</>
			)}
		</div>
	);
};

export default PromotionsController;
